/**
 * Dashboard layout model (Beta-1 plan P3-15).
 *
 *   layout = { version: 1, items: [{ i, widgetId, x, y, w, h, props }] }
 *
 *   i        — instance id, unique within the layout (`<widgetId>__<n>`); the same widget can
 *              be placed twice with different props (two Trend widgets, P3-18).
 *   widgetId — id in the WidgetRegistry.
 *   x, y     — cell position in the CANONICAL 4-column space (see sizes.js). Narrower screens
 *              never store their own coordinates: `reflowToColumns` derives them on render.
 *   w, h     — always one of the size steps (S 1×1, M 2×1, L 2×2, XL 4×2).
 *   props    — per-instance settings (scope override, period, …), validated by the widget's
 *              settingsSchema (P3-18). Layout never changes data scope beyond the user's slice.
 *
 * Everything here is pure and synchronous; persistence (Hugo_UserPreferences) is P3-19.
 */
import {
  GRID_COLUMNS,
  SIZE_ORDER,
  sizeDims,
  dimsToSize,
  dimsForColumns,
  nearestAllowedSize,
  stepSize,
} from './sizes.js';

export const LAYOUT_VERSION = 1;

/* ------------------------------------------------------------------ helpers */

function toInt(v) {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
  return Number.isInteger(n) ? n : null;
}

function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}

function cloneProps(props) {
  return props && typeof props === 'object' && !Array.isArray(props) ? { ...props } : {};
}

function cloneItem(it) {
  return { ...it, props: cloneProps(it.props) };
}

function positionsKey(items) {
  return items.map((it) => `${it.i}:${it.x},${it.y},${it.w},${it.h}`).join('|');
}

/* ------------------------------------------------------------------ basics */

/** A fresh layout object (items are copied). */
export function createLayout(items = []) {
  return { version: LAYOUT_VERSION, items: items.map(cloneItem) };
}

/** Size key of an item (`null` when its dims are not a size step). */
export function sizeOf(item) {
  return dimsToSize(item.w, item.h);
}

/** Axis-aligned rectangle overlap test; an item never overlaps itself. */
export function itemsOverlap(a, b) {
  if (a.i === b.i) return false;
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** Items sorted top-to-bottom, then left-to-right (stable). */
export function sortReadingOrder(items) {
  return [...items].sort((a, b) => a.y - b.y || a.x - b.x);
}

/** Bottom edge of the layout in rows. */
export function layoutHeight(items) {
  return items.reduce((max, it) => Math.max(max, it.y + it.h), 0);
}

/** Smallest unused `<widgetId>__<n>` (n ≥ 1) among `items`. */
export function nextItemId(widgetId, items) {
  const used = new Set(items.map((it) => it.i));
  let n = 1;
  while (used.has(`${widgetId}__${n}`)) n += 1;
  return `${widgetId}__${n}`;
}

/* ------------------------------------------------------------------ packing */

/**
 * Vertical compaction (what phone home screens and react-grid-layout do): in reading order,
 * every item floats up as far as it can without colliding; items that overlap because of bad
 * input are pushed down until they fit. Columns (x) never change.
 */
export function compactVertical(items) {
  const placed = [];
  for (const it of sortReadingOrder(items)) {
    const collidesAt = (y) => placed.some((p) => itemsOverlap({ ...it, y }, p));
    let y = Math.max(0, it.y);
    while (y > 0 && !collidesAt(y - 1)) y -= 1;
    while (collidesAt(y)) y += 1;
    placed.push({ ...it, y });
  }
  return placed;
}

/**
 * First cell (reading order) where a `w`×`h` block fits without overlapping `items`.
 * Always succeeds: the row below the lowest item is free.
 */
export function findFirstFreeSlot(items, w, h, cols = GRID_COLUMNS) {
  const width = Math.min(w, cols);
  const maxY = layoutHeight(items);
  for (let y = 0; y <= maxY; y += 1) {
    for (let x = 0; x + width <= cols; x += 1) {
      const candidate = { i: '__candidate__', x, y, w: width, h };
      if (!items.some((p) => itemsOverlap(candidate, p))) return { x, y };
    }
  }
  return { x: 0, y: maxY };
}

/**
 * Re-place `items` in the given order, each at its first free slot in the canonical grid.
 * Used to map an edit made on a 2- or 1-column grid back to the stored 4-column layout:
 * the narrow grid only tells us the new ORDER, so we repack by order (P3-16 tablet editing).
 * Ids missing from `orderedIds` keep their relative reading order at the end.
 */
export function repackFromOrder(items, orderedIds) {
  const byId = new Map(items.map((it) => [it.i, it]));
  const order = orderedIds.filter((id) => byId.has(id));
  const seen = new Set(order);
  for (const it of sortReadingOrder(items)) if (!seen.has(it.i)) order.push(it.i);
  const out = [];
  for (const id of order) {
    const it = byId.get(id);
    const { x, y } = findFirstFreeSlot(out, it.w, it.h, GRID_COLUMNS);
    out.push({ ...it, x, y });
  }
  return out;
}

/**
 * Items as they render on a grid with `cols` columns.
 *   4 → the canonical layout, compacted.
 *   2 / 1 → reading order preserved, each item downscaled (sizes.dimsForColumns) and packed
 *           into the first free slot.
 * Never persist the result — it is a projection of the canonical layout.
 */
export function reflowToColumns(items, cols) {
  if (cols >= GRID_COLUMNS) return compactVertical(items);
  const out = [];
  for (const it of sortReadingOrder(items)) {
    const size = sizeOf(it) || nearestAllowedSize(it, SIZE_ORDER);
    const { w, h } = dimsForColumns(size, cols);
    const { x, y } = findFirstFreeSlot(out, w, h, cols);
    out.push({ ...it, x, y, w, h });
  }
  return out;
}

/* ------------------------------------------------------------------ validation */

/**
 * Bring a stored layout up to what the registry allows. Never throws.
 *   - unknown widget ids are dropped (reason `unknown-widget`) — a widget renamed/removed from
 *     the registry must not break a saved layout (P3-19 test);
 *   - malformed items and duplicate ids are dropped; a missing id is assigned;
 *   - dims that are not an allowed size step for that widget snap to the nearest allowed one;
 *   - positions are clamped into the 4-column grid, then everything is compacted.
 * Returns `{ layout, dropped, fixes, changed }`; `changed` is true when the caller should
 * consider re-saving (and, for drops, telling the user — P3-19 toast).
 */
export function validateLayout(layout, registry) {
  const dropped = [];
  const fixes = [];
  const source = layout && Array.isArray(layout.items) ? layout.items : [];
  const seen = new Set();
  let items = [];

  for (const raw of source) {
    if (!raw || typeof raw !== 'object') {
      dropped.push({ i: null, widgetId: null, reason: 'malformed' });
      continue;
    }
    const widget = registry.get(raw.widgetId);
    if (!widget) {
      dropped.push({ i: raw.i ?? null, widgetId: raw.widgetId ?? null, reason: 'unknown-widget' });
      continue;
    }
    let id = typeof raw.i === 'string' && raw.i.trim() ? raw.i : null;
    if (id && seen.has(id)) {
      dropped.push({ i: id, widgetId: widget.id, reason: 'duplicate-id' });
      continue;
    }
    if (!id) {
      id = nextItemId(widget.id, [...items, ...source.filter((s) => s && typeof s.i === 'string').map((s) => ({ i: s.i }))]);
      fixes.push({ i: id, reason: 'id-assigned' });
    }
    seen.add(id);

    let w = toInt(raw.w);
    let h = toInt(raw.h);
    let size = w && h ? dimsToSize(w, h) : null;
    if (!size || !widget.sizes.includes(size)) {
      size = nearestAllowedSize({ w: w || 1, h: h || 1 }, widget.sizes) || widget.defaultSize;
      fixes.push({ i: id, reason: 'size-coerced', to: size });
    }
    ({ w, h } = sizeDims(size));

    const x0 = toInt(raw.x) ?? 0;
    const y0 = toInt(raw.y) ?? 0;
    const x = clamp(x0, 0, GRID_COLUMNS - w);
    const y = Math.max(0, y0);
    if (x !== x0 || y !== y0) fixes.push({ i: id, reason: 'position-clamped' });

    items.push({ i: id, widgetId: widget.id, x, y, w, h, props: cloneProps(raw.props) });
  }

  const before = positionsKey(sortReadingOrder(items));
  items = compactVertical(items);
  if (positionsKey(items) !== before) fixes.push({ reason: 'compacted' });

  const versionChanged = !layout || layout.version !== LAYOUT_VERSION;
  return {
    layout: { version: LAYOUT_VERSION, items },
    dropped,
    fixes,
    changed: versionChanged || dropped.length > 0 || fixes.length > 0,
  };
}

/* ------------------------------------------------------------------ edits (all return a new layout) */

/** Place a widget at its first free slot (default size unless `size` is allowed). */
export function addItem(layout, widget, { size, props = {} } = {}) {
  const key = size && widget.sizes.includes(size) ? size : widget.defaultSize;
  const { w, h } = sizeDims(key);
  const items = layout.items;
  const i = nextItemId(widget.id, items);
  const { x, y } = findFirstFreeSlot(items, w, h, GRID_COLUMNS);
  const item = { i, widgetId: widget.id, x, y, w, h, props: cloneProps(props) };
  return { layout: { version: LAYOUT_VERSION, items: [...items, item] }, item };
}

export function removeItem(layout, i) {
  return { version: LAYOUT_VERSION, items: compactVertical(layout.items.filter((it) => it.i !== i)) };
}

/** Change one item's size step (keeps its cell, nudging x left if the new width overflows). */
export function setItemSize(layout, i, sizeKey) {
  const { w, h } = sizeDims(sizeKey);
  const items = layout.items.map((it) =>
    it.i === i ? { ...it, w, h, x: clamp(it.x, 0, GRID_COLUMNS - w) } : it
  );
  return { version: LAYOUT_VERSION, items: compactVertical(items) };
}

/**
 * Compaction with one item pinned (P3-16 moves): `pinnedId` keeps exactly its cell; every other
 * item floats up in reading order but can never overlap it, so a widget dropped or nudged onto
 * another one pushes that one DOWN instead of sitting on top of it (plan P3-16 test).
 */
export function compactAround(items, pinnedId) {
  const pinned = items.find((it) => it.i === pinnedId);
  if (!pinned) return compactVertical(items);
  const placed = [{ ...pinned, y: Math.max(0, pinned.y) }];
  for (const it of sortReadingOrder(items)) {
    if (it.i === pinnedId) continue;
    const collidesAt = (y) => placed.some((p) => itemsOverlap({ ...it, y }, p));
    let y = Math.max(0, it.y);
    while (y > 0 && !collidesAt(y - 1)) y -= 1;
    while (collidesAt(y)) y += 1;
    placed.push({ ...it, y });
  }
  return placed;
}

/**
 * Put one item at a canonical cell (clamped into the grid); colliding items are pushed down.
 * Drag-and-drop in the grid is handled by react-grid-layout itself — this is the keyboard /
 * programmatic path (P3-16).
 */
export function moveItem(layout, i, { x, y }) {
  const target = layout.items.find((it) => it.i === i);
  if (!target) return layout;
  const nx = clamp(toInt(x) ?? target.x, 0, GRID_COLUMNS - target.w);
  const ny = Math.max(0, toInt(y) ?? target.y);
  if (nx === target.x && ny === target.y) return layout;
  const items = layout.items.map((it) => (it.i === i ? { ...it, x: nx, y: ny } : it));
  return { version: LAYOUT_VERSION, items: compactAround(items, i) };
}

/** Arrow-key move by whole cells in the canonical grid (P3-16 keyboard). */
export function nudgeItem(layout, i, dx, dy) {
  const target = layout.items.find((it) => it.i === i);
  if (!target) return layout;
  return moveItem(layout, i, { x: target.x + (dx || 0), y: target.y + (dy || 0) });
}

/**
 * Move one item earlier (−1) or later (+1) in reading order and repack — the keyboard move on
 * 2- and 1-column grids, where cells are a projection and only the order is meaningful.
 */
export function moveItemInOrder(layout, i, delta) {
  const order = sortReadingOrder(layout.items).map((it) => it.i);
  const idx = order.indexOf(i);
  if (idx === -1) return layout;
  const to = clamp(idx + (delta > 0 ? 1 : -1), 0, order.length - 1);
  if (to === idx) return layout;
  order.splice(idx, 1);
  order.splice(to, 0, i);
  return { version: LAYOUT_VERSION, items: repackFromOrder(layout.items, order) };
}

/** Next allowed size up (+1) or down (−1) for one item — the keyboard +/− shortcut (P3-16). */
export function stepItemSize(layout, i, direction, registry) {
  const target = layout.items.find((it) => it.i === i);
  if (!target) return layout;
  const widget = registry ? registry.get(target.widgetId) : null;
  const allowed = widget ? widget.sizes : SIZE_ORDER;
  const current = sizeOf(target) || nearestAllowedSize(target, allowed);
  const next = stepSize(current, allowed, direction);
  if (!next || next === current) return layout;
  return setItemSize(layout, i, next);
}

/**
 * Put a removed item back where it was (undo of removeItem, P3-16 snackbar). Its old
 * neighbours are pushed down if they moved into its cell; a clashing id gets a fresh one.
 */
export function restoreItem(layout, item) {
  if (!item) return layout;
  const items = layout.items;
  const i = items.some((it) => it.i === item.i) ? nextItemId(item.widgetId, items) : item.i;
  const w = Math.max(1, Math.min(GRID_COLUMNS, toInt(item.w) ?? 1));
  const restored = { ...cloneItem(item), i, w, x: clamp(toInt(item.x) ?? 0, 0, GRID_COLUMNS - w), y: Math.max(0, toInt(item.y) ?? 0) };
  return { version: LAYOUT_VERSION, items: compactAround([...items, restored], i) };
}

/** Shallow-merge `patch` into one item's props. */
export function updateItemProps(layout, i, patch) {
  return {
    version: LAYOUT_VERSION,
    items: layout.items.map((it) => (it.i === i ? { ...it, props: { ...cloneProps(it.props), ...cloneProps(patch) } } : it)),
  };
}

/**
 * Apply cell positions/dims reported by the grid (canonical 4-column space only), snapping
 * each item to a size its widget allows, then compact. `positions` = [{ i, x, y, w, h }].
 */
export function applyGridPositions(layout, positions, registry) {
  const byId = new Map(positions.map((p) => [p.i, p]));
  const items = layout.items.map((it) => {
    const p = byId.get(it.i);
    if (!p) return it;
    const widget = registry.get(it.widgetId);
    const allowed = widget ? widget.sizes : SIZE_ORDER;
    const key = nearestAllowedSize({ w: p.w, h: p.h }, allowed) || sizeOf(it) || allowed[0];
    const { w, h } = sizeDims(key);
    return { ...it, x: clamp(toInt(p.x) ?? it.x, 0, GRID_COLUMNS - w), y: Math.max(0, toInt(p.y) ?? it.y), w, h };
  });
  return { version: LAYOUT_VERSION, items: compactVertical(items) };
}

/** Structural equality (ids, cells, sizes, props) — order-insensitive. Used by Cancel (P3-16). */
export function layoutsEqual(a, b) {
  if (a === b) return true;
  if (!a || !b || (a.items || []).length !== (b.items || []).length) return false;
  const norm = (l) =>
    JSON.stringify(
      [...l.items]
        .sort((p, q) => (p.i < q.i ? -1 : p.i > q.i ? 1 : 0))
        .map((it) => [it.i, it.widgetId, it.x, it.y, it.w, it.h, sortedProps(it.props)])
    );
  return norm(a) === norm(b);
}

function sortedProps(props) {
  const p = cloneProps(props);
  return Object.keys(p)
    .sort()
    .map((k) => [k, p[k]]);
}

/** Layout items → react-grid-layout item shape, with resize bounds from the widget's sizes. */
export function toGridLayout(items, registry, cols = GRID_COLUMNS) {
  return items.map((it) => {
    const widget = registry ? registry.get(it.widgetId) : null;
    const dims = (widget ? widget.sizes : SIZE_ORDER).map((k) => dimsForColumns(k, cols));
    return {
      i: it.i,
      x: it.x,
      y: it.y,
      w: it.w,
      h: it.h,
      minW: Math.min(...dims.map((d) => d.w)),
      maxW: Math.min(cols, Math.max(...dims.map((d) => d.w))),
      minH: Math.min(...dims.map((d) => d.h)),
      maxH: Math.max(...dims.map((d) => d.h)),
    };
  });
}

/**
 * Turn the layout react-grid-layout reports after a gesture into the canonical model.
 *   4 columns  → the cells are canonical: snap sizes to allowed steps and compact.
 *   2 / 1 cols → the narrow grid only knows the new ORDER and the downscaled dims. Items whose
 *                dims differ from their projection were resized: pick the allowed canonical size
 *                that renders closest to what the user dragged; everything else keeps its size.
 *                Then repack by reading order (P3-16 tablet editing).
 * `gridLayout` = [{ i, x, y, w, h }] in the grid's column space.
 */
export function commitGridLayout(layout, gridLayout, cols, registry) {
  if (cols >= GRID_COLUMNS) return applyGridPositions(layout, gridLayout, registry);

  const projected = new Map(reflowToColumns(layout.items, cols).map((it) => [it.i, it]));
  const reported = new Map(gridLayout.map((g) => [g.i, g]));
  const resized = layout.items.map((it) => {
    const g = reported.get(it.i);
    const p = projected.get(it.i);
    if (!g || !p || (g.w === p.w && g.h === p.h)) return it;
    const widget = registry.get(it.widgetId);
    const allowed = widget ? widget.sizes : SIZE_ORDER;
    const key = nearestAllowedSize({ w: g.w, h: g.h }, allowed, cols) || sizeOf(it) || allowed[0];
    const { w, h } = sizeDims(key);
    return { ...it, w, h, x: clamp(it.x, 0, GRID_COLUMNS - w) };
  });

  const order = sortReadingOrder(gridLayout.filter((g) => reported.has(g.i))).map((g) => g.i);
  return { version: LAYOUT_VERSION, items: repackFromOrder(resized, order) };
}
